import { OntologyTypeConfigs } from 'store/utils/knowledgeGraphIcons';
import TextIcon from 'svg/knowledgeGraph/text.svg';
import { useMemo } from 'react';

// Sorting constants - defines type priority based on xsd and onto types
export const TYPE_PRIORITY = {
  'meta:KB_Class': 1,
  'meta:KB_Entity': 1,
  'onto:Customer': 1,
  'onto:Location': 1,
  'xsd:double': 2,
  'xsd:long': 2,
  'xsd:integer': 2,
  'onto:Money': 2,
  'meta:Date': 3,
  'xsd:boolean': 4,
  'xsd:string': 5
};

/**
 * Sorts child nodes based on ontology type priority then alphabetically
 * @param {Array} nodes - Array of node objects to sort
 * @returns {Array} Sorted array of nodes
 */
export const sortChildNodes = nodes => {
  return [...nodes].sort((a, b) => {
    // Get type priorities
    const typeA = TYPE_PRIORITY[a.ontologyType] ?? 1;
    const typeB = TYPE_PRIORITY[b.ontologyType] ?? 1;

    // First sort by type priority
    if (typeA !== typeB) {
      return typeA - typeB;
    }

    // Then sort alphabetically by name within same type
    return a.name.localeCompare(b.name);
  });
};

/**
 * Gets subtitle icon and text for a node based on its ontology type
 * @param {Object} node - The node object containing ontologyType
 * @param {Function} t - Translation function
 * @returns {Object} - Object containing subtitleIcon and subtitleText
 */
export function getNodeSubtitleInfo(node, t) {
  if (!node) {
    return { subtitleIcon: TextIcon, subtitleText: '' };
  }

  const ontologyType = node.ontologyType;
  const iconComponent = OntologyTypeConfigs[ontologyType]?.componentIcon || TextIcon;

  const ontologyTypeTranslationKey = ontologyType && OntologyTypeConfigs[ontologyType]?.translationKey;
  const translatedText = ontologyTypeTranslationKey ? t(`ontology.${ontologyTypeTranslationKey}`) : '';

  return {
    subtitleIcon: iconComponent,
    subtitleText: translatedText
  };
}

/**
 * React hook version that memoizes the result
 * @param {Object} node - The node object containing ontologyType
 * @param {Function} t - Translation function
 * @returns {Object} - Memoized object containing subtitleIcon and subtitleText
 */
export function useNodeSubtitleInfo(node, t) {
  return useMemo(() => getNodeSubtitleInfo(node, t), [node, t]);
}
